import { useCallback, useEffect, useState } from "react";
import { config } from "@/config";
import { PlanComparison } from "@/components/PlanComparison";
import { ConfirmModal } from "@/components/ConfirmModal";
import {
  ExpiredPage,
  IneligiblePage,
  NotRegisteredPage,
  SuccessPage,
} from "@/components/TerminalPages";
import {
  confirmMigration,
  fetchEligibility,
  fetchStatus,
  type PlanComparison as PlanComparisonData,
} from "@/services/migration";
import {
  clearAuthToken,
  loadAuthToken,
  saveAuthToken,
} from "@/utils/auth-token";

type AppState =
  | "loading"
  | "redirecting"
  | "comparison"
  | "success"
  | "expired"
  | "ineligible"
  | "not_registered";

const isCampaignExpired = (): boolean => new Date() >= config.campaignEnd;

// Reads ?token=<jwt> from current URL (set by auth-core after WhatsApp login)
// and persists it. Returns true if a token was just captured.
const captureTokenFromUrl = (): boolean => {
  const params = new URLSearchParams(window.location.search);
  const token = params.get("token");
  if (!token) return false;

  saveAuthToken(token);

  // Remove the token from the URL so it doesn't get bookmarked / shared / logged.
  params.delete("token");
  const query = params.toString();
  const newUrl =
    window.location.pathname + (query ? `?${query}` : "") + window.location.hash;
  window.history.replaceState({}, document.title, newUrl);

  return true;
};

const redirectToAuth = (): void => {
  if (!config.authUrl) {
    console.error("VITE_AUTH_URL is not configured — cannot redirect to login.");
    return;
  }
  const url = new URL(config.authUrl);
  url.searchParams.set("redirectUrl", window.location.href);
  // Riders authenticate via WhatsApp OTP; auth-core's default is Google so
  // we have to opt in explicitly. See auth-core/INTEGRATION.md.
  url.searchParams.set("method", "whatsapp");
  window.location.href = url.toString();
};

const App: React.FC = () => {
  const [state, setState] = useState<AppState>("loading");
  const [plan, setPlan] = useState<PlanComparisonData | null>(null);
  const [ineligibleVariant, setIneligibleVariant] = useState<
    "comfort_mensal" | "generic"
  >("generic");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);

  const loadEligibility = useCallback(async (): Promise<void> => {
    try {
      const result = await fetchEligibility();
      if (result.kind === "eligible") {
        setPlan(result.plan);
        setState("comparison");
      } else if (result.kind === "ineligible_comfort") {
        setIneligibleVariant("comfort_mensal");
        setState("ineligible");
      } else if (result.kind === "already_migrated") {
        setState("success");
      } else {
        // ineligible_other — any reason the backend reports that we don't have
        // a dedicated screen for. The user is authenticated, so don't bounce
        // them through re-login; show the generic "cannot migrate" page.
        setIneligibleVariant("generic");
        setState("ineligible");
      }
    } catch {
      // Network/5xx, or token rejected. If handle401 already cleared the
      // token, re-authenticate transparently. Otherwise fall back to the
      // generic ineligible screen — never the "no account found" page,
      // because at this point we know the user does have a Vammo identity.
      if (!loadAuthToken()) {
        setState("redirecting");
        redirectToAuth();
        return;
      }
      setIneligibleVariant("generic");
      setState("ineligible");
    }
  }, []);

  // Initial flow: campaign cutoff > token capture > redirect to auth-core if missing
  useEffect(() => {
    let cancelled = false;

    const init = async (): Promise<void> => {
      if (isCampaignExpired()) {
        if (!cancelled) setState("expired");
        return;
      }

      captureTokenFromUrl();
      const token = loadAuthToken();

      if (!token) {
        if (!cancelled) setState("redirecting");
        redirectToAuth();
        return;
      }

      // Authenticated — ask backend if the user has already migrated, otherwise
      // load the plan comparison.
      const status = await fetchStatus();
      if (cancelled) return;
      if (status === "success") return setState("success");
      if (status === "expired") return setState("expired");
      await loadEligibility();
    };

    init();
    return () => {
      cancelled = true;
    };
  }, [loadEligibility]);

  const handleDecideLater = useCallback((): void => {
    // User chose not to migrate now. Keep the session token (still valid)
    // and send them to the public Vammo site.
    window.location.href = "https://vammo.com";
  }, []);

  const handleSignOut = useCallback((): void => {
    clearAuthToken();
    redirectToAuth();
  }, []);

  const handleConfirm = useCallback(async (): Promise<void> => {
    setModalLoading(true);
    setModalError(null);
    try {
      await confirmMigration();
      setModalOpen(false);
      setState("success");
    } catch {
      setModalError(
        "Tente novamente em alguns instantes. Se o problema persistir, fale com o suporte."
      );
    } finally {
      setModalLoading(false);
    }
  }, []);

  return (
    <div
      className="lp-migracao"
      data-state={state}
      data-user-plan={plan?.user.planFamily ?? ""}
    >
      {state === "loading" || state === "redirecting" ? (
        <div className="page">
          <div className="page__inner">
            <div className="logo-text">Vammo</div>
            <p className="subtitle">
              {state === "redirecting"
                ? "Redirecionando para o login…"
                : "Carregando…"}
            </p>
          </div>
        </div>
      ) : state === "comparison" && plan ? (
        <>
          <PlanComparison
            plan={plan}
            onConfirm={() => {
              setModalError(null);
              setModalOpen(true);
            }}
            onBack={handleDecideLater}
          />
          <ConfirmModal
            isOpen={modalOpen}
            isLoading={modalLoading}
            error={modalError}
            onConfirm={handleConfirm}
            onCancel={() => setModalOpen(false)}
          />
        </>
      ) : state === "success" ? (
        <SuccessPage />
      ) : state === "expired" ? (
        <ExpiredPage />
      ) : state === "ineligible" ? (
        <IneligiblePage variant={ineligibleVariant} />
      ) : state === "not_registered" ? (
        <NotRegisteredPage onRetry={handleSignOut} />
      ) : null}
    </div>
  );
};

export default App;
